import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import BrandLogo from '../components/brand/BrandLogo';
import MarketingNavbar from '../components/marketing/MarketingNavbar';

const FEATURE_KEYS = ['appointments', 'marketplace', 'analytics', 'multiBranch'];
const STEP_KEYS = ['requestAccess', 'getApproved', 'launch'];
const TESTIMONIAL_KEYS = ['priya', 'james', 'aisha'];

const LandingPage = () => {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-zinc-50 dark:bg-[#07040f]">
      <MarketingNavbar />

      <section className="relative overflow-hidden border-b border-gold-500/20">
        <div className="pointer-events-none absolute inset-0 bg-aura-radial" />
        <div className="relative mx-auto flex max-w-6xl flex-col items-center px-4 py-16 text-center sm:px-6 sm:py-20 md:py-24">
          <img
            src="/brand/aura-book-logo.png"
            alt="Aura Book"
            className="h-40 w-auto object-contain drop-shadow-gold sm:h-52 md:h-60"
          />
          <p className="mt-6 font-display text-sm font-semibold tracking-[0.35em] text-gold-400 sm:text-base">
            BOOK · BEAUTY · RELAX
          </p>
          <h1 className="mt-6 max-w-3xl font-display text-3xl font-semibold tracking-tight text-zinc-900 dark:text-white sm:text-4xl md:text-5xl">
            {t('landing.hero.titlePrefix')}{' '}
            <span className="bg-gradient-to-r from-aura-300 via-glow-400 to-gold-400 bg-clip-text text-transparent">
              {t('landing.hero.titleHighlight')}
            </span>
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-base text-zinc-600 dark:text-zinc-400 sm:mt-5 sm:text-lg">
            {t('landing.hero.subtitle')}
          </p>
          <div className="mt-8 flex w-full max-w-md flex-col items-stretch gap-3 sm:mt-10 sm:max-w-none sm:flex-row sm:justify-center sm:gap-4">
            <Link
              to="/register"
              className="rounded-xl bg-gradient-to-r from-aura-600 to-glow-600 px-8 py-3 font-medium text-white shadow-brand transition hover:from-aura-500 hover:to-glow-500"
            >
              {t('landing.hero.getStarted')}
            </Link>
            <Link
              to="/login"
              className="rounded-xl border border-gold-500/40 px-8 py-3 font-medium text-gold-300 transition hover:border-gold-400 hover:bg-gold-500/10"
            >
              {t('landing.hero.seeDemo')}
            </Link>
          </div>
        </div>
      </section>

      <section className="border-t border-zinc-200/80 bg-white/60 py-20 dark:border-aura-800/60 dark:bg-aura-900/20">
        <div className="mx-auto max-w-6xl px-6">
          <h2 className="text-center font-display text-3xl font-semibold text-aura-700 dark:text-aura-200">
            {t('landing.features.title')}
          </h2>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {FEATURE_KEYS.map((key) => (
              <div
                key={key}
                className="rounded-2xl border border-zinc-200 bg-white p-6 shadow-sm dark:border-aura-800/80 dark:bg-[#0e0818]"
              >
                <h3 className="font-medium text-glow-400">{t(`landing.features.${key}.title`)}</h3>
                <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-400">
                  {t(`landing.features.${key}.description`)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="mx-auto max-w-6xl px-6">
          <h2 className="text-center font-display text-3xl font-semibold text-aura-700 dark:text-aura-200">
            {t('landing.steps.title')}
          </h2>
          <div className="mt-12 grid gap-8 md:grid-cols-3">
            {STEP_KEYS.map((key, index) => (
              <div key={key} className="text-center">
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full border border-gold-500/40 bg-glow-600/15 font-display text-lg font-semibold text-gold-300">
                  {index + 1}
                </div>
                <h3 className="mt-4 font-medium">{t(`landing.steps.${key}.title`)}</h3>
                <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-500">{t(`landing.steps.${key}.text`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-zinc-200/80 bg-white/60 py-20 dark:border-aura-800/60 dark:bg-aura-900/20">
        <div className="mx-auto max-w-6xl px-6">
          <h2 className="text-center font-display text-3xl font-semibold text-aura-700 dark:text-aura-200">
            {t('landing.testimonials.title')}
          </h2>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {TESTIMONIAL_KEYS.map((key) => (
              <blockquote
                key={key}
                className="rounded-2xl border border-zinc-200 bg-white p-6 dark:border-aura-800/80 dark:bg-[#0e0818]"
              >
                <p className="text-sm text-zinc-600 dark:text-zinc-400">
                  &ldquo;{t(`landing.testimonials.${key}.quote`)}&rdquo;
                </p>
                <footer className="mt-4">
                  <p className="font-medium text-zinc-800 dark:text-zinc-200">
                    {t(`landing.testimonials.${key}.name`)}
                  </p>
                  <p className="text-xs text-gold-500/90">
                    {t(`landing.testimonials.${key}.salon`)}
                  </p>
                </footer>
              </blockquote>
            ))}
          </div>
        </div>
      </section>

      <footer className="border-t border-gold-500/20 py-10 text-center">
        <div className="mx-auto flex max-w-6xl flex-col items-center gap-3 px-6">
          <BrandLogo to={null} size="sm" showWordmark />
          <p className="text-xs tracking-[0.2em] text-gold-500/80">YOUR SALON, JUST A CLICK AWAY</p>
          <p className="text-sm text-zinc-600 dark:text-zinc-500">
            {t('landing.footer.copyright', { year: new Date().getFullYear() })}
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
